<template>
  <span
    v-if="visible"
    class="customer-brand-logo"
    :class="[
      'customer-brand-logo--' + variant,
      'customer-brand-logo--' + context,
      'customer-brand-logo--surface-' + resolvedSurface,
    ]"
  >
    <img
      :src="source"
      :alt="alt"
      class="customer-brand-logo__image"
      @load="detectLogoContrast"
    />
  </span>
</template>

<script>
import Session from '@/handler/session'
import Branding from './branding'
import { EventBus } from '@/handler/event-bus'

export default {
  name: 'CustomerBrandLogo',
  props: {
    variant: {
      type: String,
      default: 'full',
      validator: value => ['full', 'compact'].indexOf(value) !== -1,
    },
    context: {
      type: String,
      default: 'inline',
      validator: value => ['auth', 'sidebar', 'my-space', 'dropdown', 'inline'].indexOf(value) !== -1,
    },
    alt: {
      type: String,
      default: 'Empresa',
    },
    surface: {
      type: String,
      default: 'setting',
      validator: value => ['setting', 'auto', 'light', 'dark'].indexOf(value) !== -1,
    },
    sourceOverride: {
      type: String,
      default: '',
    },
  },
  data() {
    const session = Session.getSession()
    return {
      branding: Branding.resolve(session && session.settings),
      detectedSurface: '',
    }
  },
  computed: {
    source() {
      if (this.sourceOverride) return this.sourceOverride

      if (this.variant === 'compact') {
        return this.branding.iconCustomized && this.branding.icon
          ? this.branding.icon
          : ''
      }

      if (this.branding.logoCustomized && this.branding.logo) return this.branding.logo
      if (this.branding.iconCustomized && this.branding.icon) return this.branding.icon
      return ''
    },
    visible() {
      return !!this.source
    },
    configuredSurface() {
      return this.branding.logoContrast || 'auto'
    },
    surfaceMode() {
      return this.surface === 'setting'
        ? this.configuredSurface
        : this.surface
    },
    resolvedSurface() {
      if (this.surfaceMode !== 'auto') return this.surfaceMode
      return this.detectedSurface || 'light'
    },
  },
  watch: {
    source() {
      this.detectedSurface = ''
    },
    surfaceMode() {
      this.detectedSurface = ''
    },
  },
  created() {
    EventBus.$on('Settings:settings', this.refreshBranding)
  },
  mounted() {
    this.refreshBranding()
  },
  beforeDestroy() {
    EventBus.$off('Settings:settings', this.refreshBranding)
  },
  methods: {
    refreshBranding() {
      const session = Session.getSession()
      this.branding = Branding.resolve(session && session.settings)
    },
    detectLogoContrast(event) {
      if (this.surfaceMode !== 'auto') return

      const image = event && event.target
      if (!image || !image.naturalWidth || !image.naturalHeight) return

      try {
        const canvas = document.createElement('canvas')
        const size = 48
        canvas.width = size
        canvas.height = size
        const context = canvas.getContext('2d')
        context.clearRect(0, 0, size, size)

        const scale = Math.min(size / image.naturalWidth, size / image.naturalHeight)
        const width = image.naturalWidth * scale
        const height = image.naturalHeight * scale
        const x = (size - width) / 2
        const y = (size - height) / 2
        context.drawImage(image, x, y, width, height)

        const pixels = context.getImageData(0, 0, size, size).data
        let luminance = 0
        let brightWeight = 0
        let darkWeight = 0
        let weight = 0

        for (let i = 0; i < pixels.length; i += 4) {
          const alpha = pixels[i + 3] / 255
          if (alpha < 0.08) continue

          const pixelLuminance =
            (0.2126 * pixels[i]) +
            (0.7152 * pixels[i + 1]) +
            (0.0722 * pixels[i + 2])

          luminance += pixelLuminance * alpha
          if (pixelLuminance >= 170) brightWeight += alpha
          if (pixelLuminance <= 110) darkWeight += alpha
          weight += alpha
        }

        if (!weight) {
          this.detectedSurface = 'light'
          return
        }

        if (brightWeight > darkWeight * 1.2) {
          this.detectedSurface = 'dark'
          return
        }

        if (darkWeight > brightWeight * 1.2) {
          this.detectedSurface = 'light'
          return
        }

        const average = luminance / weight
        this.detectedSurface = average > 168 ? 'dark' : 'light'
      } catch (error) {
        this.detectedSurface = 'light'
      }
    },
  },
}
</script>

<style scoped>
.customer-brand-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  box-sizing: border-box;
  padding: 6px 9px;
  border: 1px solid var(--app-color-border);
  border-radius: 8px;
  line-height: 0;
}

.customer-brand-logo--surface-light {
  background: var(--app-color-surface);
  box-shadow: 0 6px 14px rgba(11, 31, 59, 0.12);
}

.customer-brand-logo--surface-dark {
  border-color: rgba(255, 255, 255, 0.14);
  background: var(--app-color-sidebar, #0b1f3b);
  box-shadow: 0 8px 18px rgba(11, 31, 59, 0.2);
}

.customer-brand-logo__image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.customer-brand-logo--surface-light .customer-brand-logo__image {
  filter:
    drop-shadow(0 1px 1px rgba(11, 31, 59, 0.22))
    drop-shadow(0 0 1px rgba(11, 31, 59, 0.18));
}

.customer-brand-logo--surface-dark .customer-brand-logo__image {
  filter:
    drop-shadow(0 1px 1px rgba(255, 255, 255, 0.18))
    drop-shadow(0 0 1px rgba(255, 255, 255, 0.14));
}

.customer-brand-logo--auth {
  width: 132px;
  height: 48px;
}

.customer-brand-logo--my-space {
  width: 112px;
  height: 42px;
}

.customer-brand-logo--sidebar {
  width: 100%;
  height: 42px;
}

.customer-brand-logo--dropdown {
  width: 98px;
  height: 38px;
}

.customer-brand-logo--compact {
  width: 36px;
  height: 36px;
  padding: 5px;
}
</style>
